import React, { useEffect, useState } from 'react';
import { Button, Loader, Menu, Modal } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import { API, copy, showError, showSuccess } from '../../helpers';
import { assembleSSE, base64ToBlob, defaultStreamView, prettyBody, saveBlob } from './sse';
import './PayloadViewer.css';

const TABS = ['request', 'response', 'upstream'];
// Old always-on log_details rows: what the client sent, what went upstream, what came back.
const LEGACY_TABS = ['request', 'upstream_request', 'response'];

const download = (body, name) => {
  const blob = body.base64
    ? base64ToBlob(body.text, body.content_type)
    : new Blob([body.text], { type: body.content_type || 'text/plain' });
  saveBlob(blob, name);
};

const BodyView = ({ body, name, stream }) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState(stream && body && !body.base64 ? defaultStreamView(body.text) : 'raw');
  if (!body || body.size === 0) return <p className='pz-payload-empty'>{t('log.payload.empty')}</p>;
  if (body.base64) {
    return (
      <div className='pz-payload-binary'>
        <p>{t('log.payload.binary', { type: body.content_type || 'application/octet-stream', size: body.size })}</p>
        <Button size='small' onClick={() => download(body, name)}>{t('log.payload.download')}</Button>
      </div>
    );
  }
  const shown = stream && mode === 'assembled' ? assembleSSE(body.text) : prettyBody(body.text);
  return (
    <>
      <div className='pz-payload-tools'>
        {stream && (
          <div className='pz-segment'>
            {['assembled', 'raw'].map((m) => (
              <button type='button' key={m} className={mode === m ? 'active' : ''} onClick={() => setMode(m)}>
                {t('log.payload.' + m)}
              </button>
            ))}
          </div>
        )}
        <span className='pz-payload-spacer' />
        <Button size='tiny' icon='copy outline' content={t('log.payload.copy')}
          onClick={async () => { if (await copy(shown)) showSuccess(t('token.messages.copy_success')); }} />
        <Button size='tiny' icon='download' content={t('log.payload.download')} onClick={() => download(body, name)} />
      </div>
      <pre className='pz-payload-pre'>{shown}</pre>
    </>
  );
};

const PayloadViewer = ({ requestId, admin, open, onClose }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('response');

  useEffect(() => {
    if (!open || !requestId) return;
    setData(null);
    setTab('response');
    API.get(admin ? `/api/log/payload/${requestId}` : `/api/log/self/payload/${requestId}`).then((res) => {
      const { success, message, data } = (res && res.data) || {};
      if (success) setData(data);
      else {
        showError(message);
        onClose();
      }
    });
  }, [open, requestId, admin]);

  return (
    <Modal open={open} onClose={onClose} size='large' closeIcon className='pz-payload-modal'>
      <Modal.Header>{t('log.payload.title')}</Modal.Header>
      <Modal.Content scrolling>
        {!data ? (
          <Loader active inline='centered' />
        ) : (
          <>
            <p className='pz-payload-meta'>
              <code>{data.request_id}</code>{' '}
              {data.legacy
                ? t('log.payload.legacy_meta', { ms: data.elapsed_ms })
                : t('log.payload.meta', { method: data.method, path: data.path, status: data.status_code, ms: data.elapsed_ms })}
            </p>
            <Menu pointing secondary>
              {(data.legacy ? LEGACY_TABS : TABS).map((k) => (
                <Menu.Item key={k} active={tab === k} onClick={() => setTab(k)}>{t('log.payload.' + k)}</Menu.Item>
              ))}
            </Menu>
            <BodyView
              key={tab}
              body={data[tab]}
              name={`${data.request_id}-${tab}${data[tab] && !data[tab].base64 ? '.txt' : '.bin'}`}
              stream={tab !== 'request' && tab !== 'upstream_request' && data.is_stream}
            />
          </>
        )}
      </Modal.Content>
    </Modal>
  );
};

export default PayloadViewer;
